/**
 * Os arquivos excluídos na aba Documentos (E216.F4.C03, GUS-317 P6): `GET
 * /casos/{id}/documentos/excluidos` devolve, na ordem da exclusão, o arquivo, o motivo, o
 * fundamento, quem excluiu (o nome de exibição, como no histórico do caso) e quando. A listagem dos
 * documentos só traz os vivos: sem esta leitura, o excluído sumia da aba. Sem a resposta (a falha da
 * rede), a aba segue sem eles.
 */
import type { AxiosInstance } from 'axios';
import { useEffect, useState } from 'react';

import type { MotivoDaExclusaoNaMesa } from '@/lib/exclusao_na_mesa';

export interface ExcluidoNaMesaData {
  documento_id: string;
  arquivo: string;
  motivo: MotivoDaExclusaoNaMesa;
  fundamento: string;
  /** O nome de exibição de quem excluiu; `null` quando o servidor não o identifica. */
  quem: string | null;
  instante: string;
}

export interface ExcluidosNaMesaData {
  excluidos: readonly ExcluidoNaMesaData[];
}

export async function lerExcluidosNaMesa(
  client: AxiosInstance,
  caseId: string,
): Promise<readonly ExcluidoNaMesaData[]> {
  const { data } = await client.get<ExcluidosNaMesaData>(`/casos/${caseId}/documentos/excluidos`);
  return data.excluidos;
}

const NENHUM: readonly ExcluidoNaMesaData[] = [];

/** Os excluídos do caso, relidos quando `releitura` muda; nenhum enquanto lê pela primeira vez ou se falhar. */
export function useExcluidosNaMesa(
  client: AxiosInstance,
  caseId: string,
  releitura: unknown,
): readonly ExcluidoNaMesaData[] {
  const [excluidos, setExcluidos] = useState<readonly ExcluidoNaMesaData[]>(NENHUM);
  useEffect(() => {
    let vivo = true;
    lerExcluidosNaMesa(client, caseId)
      .then((lidos) => {
        if (vivo) setExcluidos(lidos);
      })
      .catch(() => {
        if (vivo) setExcluidos(NENHUM);
      });
    return () => {
      vivo = false;
    };
  }, [client, caseId, releitura]);
  return excluidos;
}
